/**
 * ENG-3470 — a support email with nothing to go on.
 *
 * A bare mailto link to `SUPPORT_EMAIL` (`../constants`) gives support exactly what the customer types, which
 * is usually not the organization, not the page they were on, and never the backend's own reference
 * id (see `apiError.ts`'s `withRef`) even when the error text already showed it. Every one of those
 * tickets starts with a round trip asking for the basics before anyone can look at a log.
 *
 * `supportMailto` pre-fills what the console already knows into the subject and body, so the first
 * message already has it. Deliberately narrow about what goes in: an organization's name and id, the
 * page, and an error reference when one is known — never the signed-in user's email or anything else
 * personal, since none of that is needed to find the log and all of it is needed to write to support
 * anyway.
 */

import { SUPPORT_EMAIL } from "../constants";

/** Kept short so an unusually long organization name can't blow up the mailto URL. */
const MAX_ORG_NAME_LENGTH = 80;

const truncate = (value: string, maxLength: number): string =>
  value.length > maxLength ? `${value.slice(0, maxLength - 1)}…` : value;

export interface SupportMailtoOptions {
  /** The organization the console was showing. Omit or pass null/undefined outside any organization. */
  orgName?: string | null;
  /** That organization's id, quoted in the body so support can look it up without asking. */
  orgId?: string | null;
  /** What page this was sent from, e.g. "Support" or "Account deleted". */
  page: string;
  /** The backend's own error reference, when one is known — the same value `withRef` would append. */
  errorRef?: string | null;
}

const present = (value: string | null | undefined): value is string =>
  typeof value === "string" && value.trim() !== "";

/** A `mailto:` link to support, pre-filled with what the console already knows. */
export function supportMailto({ orgName, orgId, page, errorRef }: SupportMailtoOptions): string {
  const subject = present(orgName) ? `Haven console – ${truncate(orgName, MAX_ORG_NAME_LENGTH)}` : "Haven console";
  const lines = [
    "", // Left blank for the person to write in.
    ...(present(orgId) ? [`Organization ID: ${orgId}`] : []),
    `Page: ${page}`,
    ...(present(errorRef) ? [`Error reference: ${errorRef}`] : []),
  ];
  // RFC 6068 body lines are CRLF-separated, and percent-encoded like a URI path, not like a form
  // body — `URLSearchParams` would send a literal "+" for every space instead of "%20", which some
  // mail clients paste in verbatim.
  const body = lines.join("\r\n");
  const query = `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return `mailto:${SUPPORT_EMAIL}?${query}`;
}
